import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  describeError,
  getOperator,
  listAgentGrants,
  problemCode,
  resendOperatorInvitation,
  resetOperatorMfa,
  resetOperatorPassword,
  revokeOperator,
  unlockAccount,
  updateOperator,
  type AgentGrant,
  type OperatorDto,
  type OperatorRole,
} from '@/lib/api';
import { AxiosError } from 'axios';
import { useAuthStore } from '@/stores/auth';
import { SensitiveCredentialModal } from '@/components/SensitiveCredentialModal';
import { SimpleConfirmDialog } from '@/components/SimpleConfirmDialog';
import { OperatorStatusBadge } from '@/components/OperatorStatusBadge';
import { PasskeysCard } from '@/components/PasskeysCard';

const ROLES: OperatorRole[] = ['SupportL1', 'SupportL2', 'Commercial', 'Admin'];

// Resend uses a separate, lighter flow (no reason). The remaining
// destructive actions still go through ReasonModal. O desbloqueio também não
// pede motivo: confirma num SimpleConfirmDialog (a API audita quem desbloqueou).
type ActionKind = 'reset-password' | 'reset-mfa' | 'revoke' | 'unlock';
type ReasonActionKind = Exclude<ActionKind, 'unlock'>;

/** Hora local `HH:mm` do fim do bloqueio. */
function formatLockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export function OperatorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const operatorClaim = useAuthStore((s) => s.operator);
  const [op, setOp] = useState<OperatorDto | null>(null);
  // Instante da leitura do operador: o bloqueio vale se `lockedUntil` estiver depois dele.
  // Lido na carga, e não no render, para o render ficar puro.
  const [loadedAt, setLoadedAt] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  // Inline edit state
  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [editingRole, setEditingRole] = useState(false);
  const [editRole, setEditRole] = useState<OperatorRole>('SupportL1');

  // Destructive-action modal state (asks for reason).
  const [pendingAction, setPendingAction] = useState<ActionKind | null>(null);
  const [reason, setReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);

  // Resend uses its own dialog (no reason).
  const [resendOpen, setResendOpen] = useState(false);

  // Concessões de agente ativas que esta conta aprovou (change platform-mcp-server, S-2.17).
  // Revogar ou rebaixar a conta derruba essas concessões em cascata na API; a tela avisa e
  // pede confirmação antes. Lidas ao abrir a revogação ou a edição de papel de um Admin.
  const [approvedGrants, setApprovedGrants] = useState<AgentGrant[]>([]);
  const [agentCheckPending, setAgentCheckPending] = useState(false);
  const [agentAck, setAgentAck] = useState(false);

  // Credential-modal payload (only set after a successful reset / resend).
  const [credentialModal, setCredentialModal] = useState<{
    verificationCode: string;
    operatorEmail: string;
    operatorFullName: string;
    contextLabel: string;
  } | null>(null);

  // `loading` derivado: a chave junta o operador e o contador de recargas, e
  // o erro so vale enquanto for o da busca que esta na tela.
  const requestKey = `${id}:${reloadTick}`;
  const loading = loadedKey !== requestKey;
  const error = loadedKey === requestKey ? loadError : null;

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    getOperator(id)
      .then((data) => {
        if (cancelled) return;
        setOp(data);
        setLoadedAt(Date.now());
        setEditName(data.fullName);
        setEditRole(data.role);
        setLoadError(null);
      })
      .catch((err) => !cancelled && setLoadError(describeError(err)))
      .finally(() => !cancelled && setLoadedKey(requestKey));
    return () => {
      cancelled = true;
    };
  }, [id, reloadTick, requestKey]);

  if (loading) return <div>Carregando…</div>;
  if (error) return <div className="error-banner">{error}</div>;
  if (!op) return <div>Operador não encontrado.</div>;

  const isSelf = operatorClaim?.id === op.id;
  const isPending = op.status === 'Pending';
  const isRevoked = op.status === 'Revoked';
  const isAdmin = operatorClaim?.role === 'Admin';
  // Bloqueio por falhas de segundo fator (C5): só no futuro conta. Uma API anterior não manda o
  // campo, e então não há bloqueio a mostrar.
  const lockedUntilMs = op.lockedUntil ? Date.parse(op.lockedUntil) : Number.NaN;
  const isLocked = Number.isFinite(lockedUntilMs) && lockedUntilMs > loadedAt;
  // A API recusa o desbloqueio da própria conta (403) e de quem não é Admin.
  const canUnlock = isAdmin && isLocked && !isSelf && !isRevoked;

  // Rebaixar é sair de Admin: a API derruba as concessões aprovadas pela conta (owner_demoted).
  const isDemoting = op.role === 'Admin' && editRole !== 'Admin';
  // Com concessões, os botões só destravam com a confirmação; enquanto a leitura não volta,
  // também ficam travados, para o aviso não chegar depois do clique.
  const agentBlocked = agentCheckPending || (approvedGrants.length > 0 && !agentAck);

  /**
   * Lê as concessões ativas e fica com as que esta conta aprovou. A rota é só de Admin e pode
   * não existir (API anterior, 404), recusar (403) ou estar sem configuração (503): em qualquer
   * falha, nenhum aviso, e a revogação segue como antes. A API revalida a cascata de todo jeito.
   */
  function loadApprovedGrants(accountId: string) {
    setAgentAck(false);
    setApprovedGrants([]);
    setAgentCheckPending(true);
    listAgentGrants({ status: 'Active' })
      .then((grants) =>
        setApprovedGrants(grants.filter((g) => g.status === 'Active' && g.approvedByAccountId === accountId)),
      )
      .catch(() => setApprovedGrants([]))
      .finally(() => setAgentCheckPending(false));
  }

  function clearApprovedGrants() {
    setApprovedGrants([]);
    setAgentAck(false);
    setAgentCheckPending(false);
  }

  async function saveName() {
    setActionError(null);
    setActionSubmitting(true);
    try {
      await updateOperator(op!.id, { fullName: editName });
      setEditingName(false);
      setReloadTick((t) => t + 1);
    } catch (err) {
      setActionError(describeFriendly(err));
    } finally {
      setActionSubmitting(false);
    }
  }

  async function saveRole() {
    if (isDemoting && agentBlocked) return;
    setActionError(null);
    setActionSubmitting(true);
    try {
      await updateOperator(op!.id, { role: editRole });
      setEditingRole(false);
      clearApprovedGrants();
      setReloadTick((t) => t + 1);
    } catch (err) {
      setActionError(describeFriendly(err));
    } finally {
      setActionSubmitting(false);
    }
  }

  async function runAction() {
    if (!pendingAction || !op) return;
    if (pendingAction === 'revoke' && agentBlocked) return;
    setActionError(null);
    setNotice(null);
    setActionSubmitting(true);
    try {
      if (pendingAction === 'unlock') {
        await unlockAccount(op.id);
        setNotice('Conta desbloqueada. O operador já pode entrar de novo.');
      } else if (pendingAction === 'reset-password') {
        const resp = await resetOperatorPassword(op.id, reason);
        setCredentialModal({
          verificationCode: resp.verificationCode,
          operatorEmail: op.email,
          operatorFullName: op.fullName,
          contextLabel: 'Senha redefinida — convite reenviado',
        });
      } else if (pendingAction === 'reset-mfa') {
        await resetOperatorMfa(op.id, reason);
      } else if (pendingAction === 'revoke') {
        await revokeOperator(op.id, reason);
        navigate('/operators');
        return;
      }
      setPendingAction(null);
      setReason('');
      setReloadTick((t) => t + 1);
    } catch (err) {
      setActionError(describeFriendly(err));
      // O diálogo do desbloqueio não tem onde mostrar o erro: fecha e deixa o aviso na página.
      if (pendingAction === 'unlock') setPendingAction(null);
    } finally {
      setActionSubmitting(false);
    }
  }

  async function runResend() {
    if (!op) return;
    setActionError(null);
    setNotice(null);
    setActionSubmitting(true);
    try {
      const resp = await resendOperatorInvitation(op.id);
      setCredentialModal({
        verificationCode: resp.verificationCode,
        operatorEmail: op.email,
        operatorFullName: op.fullName,
        contextLabel: 'Convite reenviado',
      });
      setResendOpen(false);
      setReloadTick((t) => t + 1);
    } catch (err) {
      setActionError(describeResendFriendly(err));
      setResendOpen(false);
    } finally {
      setActionSubmitting(false);
    }
  }

  return (
    <div className="page" style={{ maxWidth: 900 }}>
      <Link to="/operators" className="backlink">← Operadores</Link>
      <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>
        {op.fullName}
      </h2>

      <div className="card" style={{ marginBottom: 16 }}>
        <Row label="Email" value={op.email} />
        <Row
          label="Nome"
          value={
            editingName ? (
              <span className="btn-row">
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  maxLength={200}
                />
                <button className="btn btn-secondary" onClick={saveName} disabled={actionSubmitting}>
                  Salvar
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setEditName(op.fullName);
                    setEditingName(false);
                  }}
                >
                  Cancelar
                </button>
              </span>
            ) : (
              <span>
                {op.fullName}
                {!isRevoked && (
                  <button
                    type="button"
                    onClick={() => setEditingName(true)}
                    style={{
                      marginLeft: 8,
                      background: 'none',
                      border: 'none',
                      color: 'var(--primary, #F97316)',
                      cursor: 'pointer',
                    }}
                  >
                    editar
                  </button>
                )}
              </span>
            )
          }
        />
        <Row
          label="Papel"
          value={
            editingRole ? (
              <span style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span className="btn-row">
                  <select
                    aria-label="Papel"
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value as OperatorRole)}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-secondary"
                    onClick={saveRole}
                    disabled={actionSubmitting || (isDemoting && agentBlocked)}
                  >
                    Salvar
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setEditRole(op.role);
                      setEditingRole(false);
                      clearApprovedGrants();
                    }}
                  >
                    Cancelar
                  </button>
                </span>
                {isDemoting && approvedGrants.length > 0 && (
                  <AgentGrantsWarning grants={approvedGrants} acknowledged={agentAck} onAcknowledge={setAgentAck} />
                )}
              </span>
            ) : (
              <span>
                <span className="badge">{op.role}</span>
                {!isRevoked && !isSelf && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingRole(true);
                      if (op.role === 'Admin') loadApprovedGrants(op.id);
                    }}
                    style={{
                      marginLeft: 8,
                      background: 'none',
                      border: 'none',
                      color: 'var(--primary, #F97316)',
                      cursor: 'pointer',
                    }}
                  >
                    editar
                  </button>
                )}
              </span>
            )
          }
        />
        <Row
          label="Status"
          value={
            <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <OperatorStatusBadge
                status={op.status}
                pendingExpiresAt={op.pendingInvitationExpiresAt}
              />
              {isLocked && op.lockedUntil && (
                <span
                  className="badge"
                  data-testid="operator-locked-badge"
                  title={`Bloqueada por falhas de segundo fator até ${new Date(op.lockedUntil).toLocaleString('pt-BR')}`}
                >
                  Bloqueada até {formatLockTime(op.lockedUntil)}
                </span>
              )}
            </span>
          }
        />
        {!isRevoked && op.invitationCount > 1 && (
          <Row
            label="Reenvios"
            value={`${op.invitationCount - 1} (excluindo o convite original)`}
          />
        )}
        {!isRevoked && op.lastInvitationIssuedAt && (
          <Row
            label="Último convite enviado"
            value={new Date(op.lastInvitationIssuedAt).toLocaleString('pt-BR')}
          />
        )}
        <Row
          label="Criado em"
          value={new Date(op.createdAt).toLocaleString('pt-BR')}
        />
        <Row
          label="Último login"
          value={op.lastLoginAt ? new Date(op.lastLoginAt).toLocaleString('pt-BR') : '—'}
        />
        {op.revokedAt && (
          <Row label="Revogado em" value={new Date(op.revokedAt).toLocaleString('pt-BR')} />
        )}
      </div>

      {actionError && <div className="error-banner">{actionError}</div>}
      {notice && (
        <div className="info-banner" role="status">
          {notice}
        </div>
      )}

      {!isRevoked && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {canUnlock && (
            <button className="btn btn-secondary" onClick={() => setPendingAction('unlock')}>
              Desbloquear
            </button>
          )}
          {isPending && (
            <button
              className="btn"
              onClick={() => setResendOpen(true)}
              disabled={isSelf}
              style={{ background: 'var(--primary, #F97316)', color: '#fff', padding: '8px 16px', borderRadius: 8 }}
            >
              Reenviar convite
            </button>
          )}
          {!isPending && (
            <>
              <button
                className="btn btn-secondary"
                onClick={() => setPendingAction('reset-password')}
                disabled={isSelf}
                title={isSelf ? 'Use outro admin para esta operação' : undefined}
              >
                Reset Senha
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setPendingAction('reset-mfa')}
                disabled={isSelf}
                title={isSelf ? 'Use outro admin para esta operação' : undefined}
              >
                Reset MFA
              </button>
            </>
          )}
          <button
            className="btn"
            onClick={() => {
              setPendingAction('revoke');
              loadApprovedGrants(op.id);
            }}
            disabled={isSelf}
            style={{ background: '#DC2626', color: '#fff', padding: '8px 16px', borderRadius: 8 }}
            title={isSelf ? 'Use outro admin para esta operação' : undefined}
          >
            Revogar
          </button>
        </div>
      )}

      {/* Passkeys da conta (C14): o Admin confere e revoga a perdida; o cadastro é do próprio operador. */}
      {op.status !== 'Pending' && <PasskeysCard mode="account" accountId={op.id} />}

      {resendOpen && (
        <SimpleConfirmDialog
          title="Reenviar convite"
          body={
            <>
              Reenviar convite para <strong>{op.email}</strong>? Esta ação invalida o link
              anterior e dispara novo email com novo código.
            </>
          }
          confirmLabel="Reenviar"
          submitting={actionSubmitting}
          onConfirm={runResend}
          onCancel={() => setResendOpen(false)}
        />
      )}

      {pendingAction === 'unlock' && (
        <SimpleConfirmDialog
          title="Desbloquear conta"
          body={
            <>
              Desbloquear <strong>{op.email}</strong>? O bloqueio por falhas de segundo fator sai
              agora e o operador pode entrar de novo. A ação fica registrada na auditoria.
            </>
          }
          confirmLabel="Desbloquear"
          submitting={actionSubmitting}
          onConfirm={runAction}
          onCancel={() => setPendingAction(null)}
        />
      )}

      {pendingAction && pendingAction !== 'unlock' && (
        <ReasonModal
          actionKind={pendingAction}
          reason={reason}
          setReason={setReason}
          submitting={actionSubmitting}
          blocked={pendingAction === 'revoke' && agentBlocked}
          extra={
            pendingAction === 'revoke' && approvedGrants.length > 0 ? (
              <AgentGrantsWarning grants={approvedGrants} acknowledged={agentAck} onAcknowledge={setAgentAck} />
            ) : null
          }
          onCancel={() => {
            setPendingAction(null);
            setReason('');
            setActionError(null);
            clearApprovedGrants();
          }}
          onConfirm={runAction}
        />
      )}

      {credentialModal && (
        <SensitiveCredentialModal
          verificationCode={credentialModal.verificationCode}
          operatorEmail={credentialModal.operatorEmail}
          operatorFullName={credentialModal.operatorFullName}
          contextLabel={credentialModal.contextLabel}
          onConfirm={() => setCredentialModal(null)}
        />
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="detail-grid" style={{ padding: '10px 0', borderBottom: '1px solid var(--color-border)' }}>
      <div style={{ color: '#64748B', fontSize: 13 }}>{label}</div>
      <div>{value}</div>
    </div>
  );
}

function ReasonModal({
  actionKind,
  reason,
  setReason,
  submitting,
  blocked = false,
  extra = null,
  onCancel,
  onConfirm,
}: {
  actionKind: ReasonActionKind;
  reason: string;
  setReason: (r: string) => void;
  submitting: boolean;
  /** Trava o Confirmar por uma condição de fora (ex.: concessões de agente sem confirmação). */
  blocked?: boolean;
  /** Conteúdo extra antes dos botões (ex.: o aviso de concessões de agente). */
  extra?: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const titles: Record<ReasonActionKind, string> = {
    'reset-password': 'Confirmar reset de senha',
    'reset-mfa': 'Confirmar reset de MFA',
    revoke: 'Confirmar revogação',
  };
  const reasonValid = reason.length >= 5 && reason.length <= 500;

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 40,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reason-modal-title"
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 24,
          maxWidth: 420,
          width: '90%',
          boxShadow: '0 24px 48px rgba(0,0,0,0.25)',
        }}
      >
        <h2 id="reason-modal-title" style={{ marginTop: 0, fontSize: 18 }}>
          {titles[actionKind]}
        </h2>
        <div className="field">
          <label htmlFor="reason-input">Motivo (5–500 caracteres)</label>
          <textarea
            id="reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            required
          />
          <div style={{ fontSize: 12, color: '#64748B' }}>
            {reason.length} / 500 caracteres
          </div>
        </div>
        {extra}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn"
            disabled={!reasonValid || submitting || blocked}
            onClick={onConfirm}
            style={{
              background: actionKind === 'revoke' ? '#DC2626' : 'var(--primary, #F97316)',
              color: '#fff',
              padding: '8px 16px',
              borderRadius: 8,
            }}
          >
            {submitting ? 'Confirmando…' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`;
}

/**
 * Aviso de cascata (D28, "Cascata da conta"): revogar ou rebaixar a conta revoga as concessões
 * de agente que ela aprovou. Só aparece com pelo menos uma; a confirmação é obrigatória.
 */
function AgentGrantsWarning({
  grants,
  acknowledged,
  onAcknowledge,
}: {
  grants: AgentGrant[];
  acknowledged: boolean;
  onAcknowledge: (value: boolean) => void;
}) {
  const profiles = joinNames(Array.from(new Set(grants.map((g) => g.profileName))));
  const count = grants.length;
  return (
    <div className="warning-banner" role="alert" data-testid="agent-grants-warning" style={{ marginTop: 8 }}>
      <p style={{ margin: '0 0 8px' }}>
        Esta conta aprovou {count} {count === 1 ? 'concessão ativa' : 'concessões ativas'} de agente; revogá-la ou
        rebaixá-la derruba {count === 1 ? 'o perfil' : 'os perfis'} {profiles}.
      </p>
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
        <input type="checkbox" checked={acknowledged} onChange={(e) => onAcknowledge(e.target.checked)} />
        <span>Entendo que {count === 1 ? 'o perfil perde a concessão' : 'esses perfis perdem a concessão'} e que o Hermes vai precisar de um novo Authenticate.</span>
      </label>
    </div>
  );
}

function describeFriendly(err: unknown): string {
  const code = problemCode(err);
  if (code === 'platform.accounts.last_admin') {
    return 'Não é possível remover o último admin ativo.';
  }
  if (code === 'platform.concurrency.exhausted') {
    return 'Sistema ocupado, tente novamente em alguns segundos.';
  }
  return describeError(err);
}

function describeResendFriendly(err: unknown): string {
  if (err instanceof AxiosError) {
    const code = problemCode(err);
    if (code === 'platform.accounts.resend_rate_limited') {
      const data = err.response?.data as { retryAfterSeconds?: number } | undefined;
      const seconds = data?.retryAfterSeconds ?? 0;
      const minutes = Math.max(1, Math.ceil(seconds / 60));
      return `Limite de reenvios atingido para esta conta. Tente novamente em ~${minutes} min ou use "Reset Senha" como alternativa.`;
    }
    const status = err.response?.status ?? 0;
    if (status === 422) return 'Operador já está ativo — não é mais necessário reenviar convite.';
    if (status === 404) return 'Operador não encontrado.';
  }
  return describeFriendly(err);
}
